import { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, AlertOctagon, Check, ExternalLink, MessageSquareReply, Send, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Top-bar Ping bell — now visible to both roles:
 *   Member    → sees admin nudges + can reply inline
 *   SuperAdmin→ sees replies coming back from members
 * Auto-polls every 30s so the badge count is live.
 */
export default function PingBell() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const isManagerOrAdmin = isAdmin || user?.role === "manager";
  const [pings, setPings] = useState([]);
  const [open, setOpen] = useState(false);
  const [replyFor, setReplyFor] = useState(null);   // ping id currently being replied to (members only)
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pings");
      setPings(Array.isArray(data) ? data : []);
    } catch {
      setPings([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const ack = async (id) => {
    try {
      await api.post(`/pings/${id}/ack`);
      setPings((prev) => prev.filter((p) => p.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ackAll = async () => {
    try {
      await api.post(`/pings/ack-all`);
      setPings([]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openLead = (lead_id, ping_id) => {
    setOpen(false);
    ack(ping_id);
    navigate(`/dashboard/leads?open=${lead_id}`);
  };

  const startReply = (ping_id) => {
    setReplyFor(ping_id);
    setReplyText("");
  };

  const sendReply = async (ping_id) => {
    const msg = replyText.trim();
    if (!msg) { toast.error("Type a reply before sending"); return; }
    setSending(true);
    try {
      await api.post(`/pings/${ping_id}/reply`, { message: msg });
      toast.success("Reply sent to SuperAdmin");
      setPings((prev) => prev.filter((p) => p.id !== ping_id));   // original is now read
      setReplyFor(null);
      setReplyText("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSending(false); }
  };

  const total = pings.length;
  const hasAny = total > 0;

  // Role-aware copy for the dropdown header.
  const replyCount = pings.filter((p) => p.kind === "reply").length;
  const nudgeCount = total - replyCount;
  const headerTitle = isManagerOrAdmin
    ? (replyCount && !nudgeCount
        ? `${replyCount} repl${replyCount === 1 ? "y" : "ies"} from your team`
        : nudgeCount && !replyCount
          ? `${nudgeCount} ping${nudgeCount === 1 ? "" : "s"} for you`
          : `${total} ping${total === 1 ? "" : "s"} · ${replyCount} repl${replyCount === 1 ? "y" : "ies"}`)
    : `${total} ping${total === 1 ? "" : "s"} from your leaders`;
  const emptyMsg = isManagerOrAdmin
    ? "No new pings or replies. Nudges and team responses will land here."
    : "You're all caught up. New pings will land here.";
  const subtitle = hasAny
    ? (isManagerOrAdmin
        ? "Members responding to your nudges — plus any pings sent to you."
        : "Review each nudge and follow up on the flagged lead(s).")
    : emptyMsg;

  return (
    <div className="relative" ref={ref} data-testid="ping-bell-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative w-8 h-8 rounded-md border flex items-center justify-center transition-colors
          ${hasAny
            ? "bg-rose-500/20 border-rose-300/40 text-rose-100 hover:bg-rose-500/30"
            : "bg-white/10 border-white/20 text-white/80 hover:bg-white/20"}`}
        aria-label={`Pings${hasAny ? ` (${total} unread)` : ""}`}
        data-testid="ping-bell-btn"
      >
        <Bell className="w-4 h-4" />
        {hasAny && (
          <>
            <span
              className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center border border-white/50 shadow-sm shadow-rose-300/40"
              data-testid="ping-bell-badge"
            >
              {total > 99 ? "99+" : total}
            </span>
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full bg-rose-400 opacity-70 animate-ping" aria-hidden="true" />
          </>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[400px] max-w-[92vw] max-h-[70vh] overflow-hidden rounded-xl border-2 border-rose-300 bg-white shadow-2xl shadow-rose-200/30 z-50 flex flex-col"
          data-testid="ping-bell-dropdown"
          role="dialog"
        >
          {/* header */}
          <div className="flex items-start gap-3 p-4 border-b border-rose-100 bg-gradient-to-br from-rose-50 to-rose-100/60">
            <div className="w-9 h-9 rounded-lg bg-rose-500 flex items-center justify-center flex-shrink-0 shadow-sm">
              <AlertOctagon className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-sm font-semibold text-rose-900">{headerTitle}</p>
                {hasAny && <span className="text-[10px] text-rose-500 uppercase tracking-widest">{isAdmin ? "responses in" : "action required"}</span>}
              </div>
              <p className="text-[11px] text-rose-700 mt-0.5">{subtitle}</p>
            </div>
            {hasAny && (
              <button
                onClick={ackAll}
                className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 flex-shrink-0"
                data-testid="ping-bell-ack-all"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* list */}
          <div className="overflow-y-auto flex-1">
            {!hasAny && (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 mx-auto text-slate-300" />
                <p className="text-xs text-slate-400 mt-2">{emptyMsg}</p>
              </div>
            )}
            <ul className="divide-y divide-rose-100">
              {pings.map((p) => {
                const isReply = p.kind === "reply";
                const showReplyBox = replyFor === p.id;
                return (
                  <li
                    key={p.id}
                    className={`p-3 hover:bg-rose-50/40 transition-colors ${isReply ? "bg-indigo-50/40" : ""}`}
                    data-testid={`ping-bell-row-${p.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-900 truncate">{p.lead_name || "(no lead)"}</p>
                          <span
                            className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-semibold ${
                              isReply
                                ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                                : "bg-rose-100 text-rose-700 border border-rose-200"
                            }`}
                          >
                            {isReply ? "reply" : "ping"}
                          </span>
                          <span className="text-[10px] text-slate-500">{isReply ? `${p.from_user_name} replied` : `from ${p.from_user_name}`}</span>
                        </div>
                        {p.message && (
                          <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap break-words">{p.message}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(p.created_at).toLocaleString()}</p>

                        {/* Members (and managers viewing pings addressed to them) can reply inline */}
                        {!isReply && showReplyBox && (
                          <div className="mt-2 flex items-start gap-2" data-testid={`ping-reply-form-${p.id}`}>
                            <textarea
                              autoFocus
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Reply…"
                              rows={2}
                              className="flex-1 text-sm bg-white border border-rose-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-500 resize-none placeholder:text-rose-300 font-medium"
                              style={{ color: "#e11d48", caretColor: "#e11d48" }}
                              data-testid={`ping-reply-input-${p.id}`}
                            />
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => sendReply(p.id)}
                                disabled={sending}
                                className="w-7 h-7 rounded-md bg-rose-500 text-white hover:bg-rose-600 flex items-center justify-center disabled:opacity-50"
                                aria-label="Send reply"
                                data-testid={`ping-reply-send-${p.id}`}
                                title="Send"
                              >
                                <Send className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => { setReplyFor(null); setReplyText(""); }}
                                className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
                                aria-label="Cancel"
                                data-testid={`ping-reply-cancel-${p.id}`}
                                title="Cancel"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {p.lead_id && (
                          <button
                            onClick={() => openLead(p.lead_id, p.id)}
                            className="text-[11px] font-medium text-rose-700 hover:text-rose-900 flex items-center gap-1"
                            data-testid={`ping-bell-open-${p.id}`}
                            title="Open lead"
                          >
                            Open <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                        {/* Anyone (member or manager) can reply to a nudge that was sent to them */}
                        {!isReply && !showReplyBox && (
                          <button
                            onClick={() => startReply(p.id)}
                            className="w-6 h-6 rounded-md hover:bg-rose-100 flex items-center justify-center text-rose-600"
                            aria-label="Reply"
                            data-testid={`ping-bell-reply-${p.id}`}
                            title="Reply"
                          >
                            <MessageSquareReply className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => ack(p.id)}
                          className="w-6 h-6 rounded-md hover:bg-rose-100 flex items-center justify-center text-rose-600"
                          aria-label="Acknowledge"
                          data-testid={`ping-bell-ack-${p.id}`}
                          title="Mark read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
